/**
 * BoothAtmosphereProvider — the kiosk page-shell wrapper.
 *
 * Owns:
 *   • the radial-gradient page background tied to the active theme
 *   • the optional fullscreen affordance (desktop kiosks)
 *   • auto-fullscreen on the FIRST guest gesture (Android / Samsung
 *     / Chrome) so the browser chrome retracts the moment the
 *     experience begins. iOS Safari does not support the Fullscreen
 *     API on arbitrary elements; on iOS the gesture is a calm no-op.
 *   • the global keyframes / scrollbar / tap-highlight resets that
 *     every booth surface depends on
 *
 * Does NOT own:
 *   • theme state (lives in useBoothTheme)
 *   • idle attract timing (lives in useBoothIdleTimer)
 *   • view routing (lives in BoothOSPage)
 *
 * Theme is passed in as a prop — no context introduced, in line
 * with the museum-conservation rule against new abstractions
 * during this refactor.
 */
import React, { useEffect, useState } from 'react';
import { SERIF } from './boothShellAtoms';

const FullscreenAffordance = ({ theme }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const sync = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  const toggle = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="booth-fullscreen-toggle"
      className="booth-fullscreen-affordance"
      style={{
        position: 'absolute', top: 16, right: 16, zIndex: 50,
        background: 'transparent', border: `1px solid ${theme.hair}`,
        color: theme.inkMuted, fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
        padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
      }}
    >
      {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
    </button>
  );
};

/**
 * On the first guest gesture inside the booth shell, request
 * fullscreen on the document element. Browsers REQUIRE a user
 * gesture for requestFullscreen() — we attach a one-shot capture
 * listener so any tap (launcher card, atmosphere chip, anywhere)
 * retracts the browser chrome immediately.
 *
 * Constraints:
 *   • Only fire once per session-tab. If the guest exits fullscreen
 *     (Android pull-down, Esc) we respect that intent and never
 *     re-request — re-firing on every subsequent tap would feel
 *     hostile.
 *   • Silent failure on iOS Safari (no element-level Fullscreen
 *     API). The booth experience still works fine; the URL bar
 *     just stays visible there.
 */
const useAutoFullscreenOnFirstGesture = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (typeof document === 'undefined') return undefined;
    // requestFullscreen is missing on iOS Safari for non-video
    // elements. Skip the listener entirely on those platforms.
    const root = document.documentElement;
    if (!root?.requestFullscreen) return undefined;
    // Idempotent across remounts within the same tab — we never
    // want a back-button bounce to re-summon fullscreen against
    // a guest who deliberately exited.
    try {
      if (sessionStorage.getItem('booth_os_fs_attempted') === '1') return undefined;
    } catch { /* private mode */ }

    const onFirstGesture = () => {
      try { sessionStorage.setItem('booth_os_fs_attempted', '1'); } catch {}
      // Already fullscreen (operator pre-fullscreened the kiosk) →
      // nothing to do.
      if (document.fullscreenElement) return;
      root.requestFullscreen({ navigationUI: 'hide' }).catch(() => {
        // Browser said no (permission policy, iframe sandbox, etc).
        // Calm no-op — the experience does not depend on it.
      });
    };

    // Capture phase so we win the race against any child
    // pointerdown handler that calls stopPropagation. `once: true`
    // makes the listener self-detach after a single fire.
    const opts = { capture: true, once: true, passive: true };
    window.addEventListener('pointerdown', onFirstGesture, opts);
    window.addEventListener('touchstart', onFirstGesture, opts);

    return () => {
      window.removeEventListener('pointerdown', onFirstGesture, opts);
      window.removeEventListener('touchstart', onFirstGesture, opts);
    };
  }, []);
};

const BoothAtmosphereProvider = ({ theme, fullscreenable, children }) => {
  useAutoFullscreenOnFirstGesture();
  return (
    <div
      data-testid="booth-os-shell"
      style={{
        minHeight: '100vh',
        width: '100%',
        background: `radial-gradient(1300px 800px at 50% -120px, ${theme.bgHi} 0%, ${theme.bg} 60%, #0A0604 100%)`,
        color: theme.ink,
        fontFamily: SERIF,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {fullscreenable && <FullscreenAffordance theme={theme} />}
      {children}
      <style>{`
        @keyframes editorialFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes settle      { from { opacity: 0; } to { opacity: 1; } }
        *::-webkit-scrollbar { display: none; }
        * { -webkit-tap-highlight-color: transparent; }
        input, textarea, button { font-family: inherit; }
        input::placeholder, textarea::placeholder { color: rgba(255,235,210,0.32); }
        /* Booth OS is a kiosk-first surface — on mobile widths the
           FullscreenAffordance has no real value (auto-fullscreen
           on first gesture covers it) and it visually crashes into
           the FlowFrame eyebrow header. Hide it there. */
        @media (max-width: 640px) {
          .booth-fullscreen-affordance { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default BoothAtmosphereProvider;
